import { BackLink } from '../components/kit/BackLink'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  api,
  can,
  programmeReport,
  type ChainVerdict,
  type HomeClock,
  type HomePanel,
  type HomePeriodKey,
  type Me,
  type ProgrammeReport as Report,
} from '../lib/api'
import { HEALTH_PART, HEALTH_RULE, healthPoints } from '../lib/healthScore'
import { ErrorBox } from '../components/DataTable'
import { Skeleton } from '../components/ui'
import { formatIN } from '../components/kit'
import {
  BAND_COLOR,
  ClockRing,
  DeadlineStrip,
  Donut,
  Legend,
  Pipeline,
  Ring,
  Sparkline,
  StackBar,
  TierGrid,
  TooltipProvider,
  WeeklyColumns,
  type DonutSlice,
} from '../components/charts'
import {
  AgeingBars,
  EvidenceMeter,
  FigureCard,
  SEVERITIES,
  SeverityBars,
  SeverityKey,
  StageCounts,
  TopVendorsTable,
} from '../components/report'
import '../styles/report.css'
import '../styles/exceptions.css'

// The programme report: a board pack a risk committee can be handed, printed or
// saved as a PDF from the browser.
//
// # WHERE EVERY FIGURE COMES FROM
//
// Nothing is counted here. The pack reads three answers: GET
// /v1/reports/programme for what only the report carries (the figures beside
// their previous value, the headlines, the top vendors, findings by age, the
// late and due lists, the exits and the method), GET /v1/home/panel for the
// charts Home draws (health, the risk mix, the tier grid, the findings trend,
// the pipeline, the deadlines), so the pack and the Home it was generated from
// cannot disagree, and GET /v1/evidence/verify for the chain head. A part an
// answer left out, because the reader may not see it, is left out of the page
// with no empty shell; a part that was sent and holds nothing says so in a line.
//
// # WHY A PAGE AND NOT A FILE
//
// The browser's print dialog already writes a PDF, a page keeps the charts as
// vectors, and the header and footer come from @page margin boxes. CSS cannot
// read the customer's name out of the page, so the footer's rule is written as
// a style element when the name is known.

const PERIODS: { key: HomePeriodKey; label: string }[] = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: 'Quarter' },
]

// The distance between two points of the panel's health trend, in days, as
// packages/core/repo/home_panel.go steps it.
const STEP_DAYS: Record<HomePeriodKey, number> = { '7d': 1, '30d': 2.5, '90d': 7 }

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// The findings trend the panel sends once its period scopes the chart. Older
// panels send only the twelve weeks, which the report falls back to.
type TrendBucket = { start: string; label: string; raised: number; closed: number; open: number }
type Panel = HomePanel & {
  findingsTrend?: { unit: 'day' | 'week'; buckets: TrendBucket[]; stillOpen: number; href: string }
}

type Chain = { verdict: ChainVerdict; at: Date } | { refused: string } | null

function periodFromHash(): HomePeriodKey {
  const q = new URLSearchParams(window.location.hash.split('?')[1] ?? '')
  const p = q.get('period')
  return p === '7d' || p === '90d' ? p : '30d'
}

function istParts(iso: string | Date) {
  const d = typeof iso === 'string' ? new Date(iso) : iso
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  return { day: get('day'), month: MONTHS[Number(get('month')) - 1] ?? '', year: get('year'), hour: get('hour'), minute: get('minute') }
}

// "16 Aug"
function dayMonth(iso: string | Date): string {
  const p = istParts(iso)
  return p.day + ' ' + p.month
}

// "16 Aug 2026"
function fullDate(iso: string | Date): string {
  const p = istParts(iso)
  return p.day + ' ' + p.month + ' ' + p.year
}

// A date the API sends as a calendar day, which is not moved into another zone.
function calendarDate(day: string): string {
  const [y, m, d] = day.slice(0, 10).split('-')
  return (d ?? '') + ' ' + (MONTHS[Number(m) - 1] ?? '') + ' ' + (y ?? '')
}

// "16 Sep 2026, 14:35 IST"
function istStamp(iso: string | Date): string {
  const p = istParts(iso)
  return p.day + ' ' + p.month + ' ' + p.year + ', ' + p.hour + ':' + p.minute + ' IST'
}

// "17 Aug to 16 Sep 2026", with the first year written only when it differs.
function span(from: string, to: string): string {
  const a = istParts(from)
  const b = istParts(to)
  return a.day + ' ' + a.month + (a.year === b.year ? '' : ' ' + a.year) + ' to ' + b.day + ' ' + b.month + ' ' + b.year
}

function plural(n: number, one: string, many: string): string {
  return formatIN(n) + ' ' + (n === 1 ? one : many)
}

// A string written into a CSS rule: backslashes, quotes and line breaks
// escaped, so a customer name cannot end the rule it is written into.
function cssString(s: string): string {
  return '"' + s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/[\r\n]+/g, ' ') + '"'
}

const MARGIN_FONT = 'font: 500 8pt "Inter Variable", Inter, system-ui, sans-serif; color: #5b6478;'

function pageRules(customer: string): string {
  return (
    '@page { size: A4 portrait; margin: 20mm 16mm 18mm; ' +
    '@top-left { content: "FOCTTA TPRM | Third-party risk programme report"; ' +
    MARGIN_FONT +
    ' vertical-align: bottom; padding-bottom: 5mm; } ' +
    '@bottom-left { content: ' +
    cssString('Confidential | ' + customer + ' | Page ') +
    ' counter(page) " of " counter(pages); ' +
    MARGIN_FONT +
    ' vertical-align: top; padding-top: 5mm; } } ' +
    '@page :first { margin: 0; @top-left { content: none; } @bottom-left { content: none; } }'
  )
}

export function ProgrammeReport({ me }: { me: Me }) {
  const [period, setPeriod] = useState<HomePeriodKey>(periodFromHash)
  const [panel, setPanel] = useState<Panel | null>(null)
  const [report, setReport] = useState<Report | null>(null)
  const [reportFailed, setReportFailed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [chain, setChain] = useState<Chain>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    let live = true
    setLoading(true)
    Promise.all([
      api.homePanel(period),
      // An API from before the report read answers 404 here; the pack is then
      // drawn from the panel alone and says so.
      programmeReport(period).then(
        (r) => ({ r, failed: false }),
        () => ({ r: null, failed: true }),
      ),
    ])
      .then(([p, rep]) => {
        if (!live) return
        setPanel(p)
        setReport(rep.r)
        setReportFailed(rep.failed)
        setLoading(false)
      })
      .catch((e) => live && setError(e))
    return () => {
      live = false
    }
  }, [period])

  useEffect(() => {
    let live = true
    if (can('audit.read')) {
      api
        .verifyChain()
        .then((v) => live && setChain({ verdict: v, at: new Date() }))
        .catch(() => live && setChain({ refused: 'The evidence chain could not be read when this report was generated.' }))
    } else {
      setChain({ refused: 'The evidence chain is read by the people who hold the audit trail, and this report was prepared without it.' })
    }
    return () => {
      live = false
    }
  }, [])

  const customer = report?.tenant.name || me.tenant?.displayName || me.tenant?.legalName || 'This organisation'

  // The printed page: A4 portrait, the running header top left, the footer
  // naming the customer with the page number, and the cover edge to edge with
  // neither. Written here rather than in report.css because @page cannot be
  // scoped to one screen and the stylesheet stays loaded after the report
  // closes, and because CSS cannot read the customer's name out of the page.
  useEffect(() => {
    const el = document.createElement('style')
    el.setAttribute('data-report-page', '')
    el.textContent = pageRules(customer)
    document.head.appendChild(el)
    return () => el.remove()
  }, [customer])

  // The toolbar sticks under the console's own header, whatever height that
  // header has at this width.
  useEffect(() => {
    const top = document.querySelector('.topline') as HTMLElement | null
    const root = document.querySelector('.rpt') as HTMLElement | null
    if (!top || !root) return
    const sync = () => root.style.setProperty('--rpt-top', top.offsetHeight + 'px')
    sync()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null
    ro?.observe(top)
    return () => ro?.disconnect()
  }, [panel])

  const choose = (p: HomePeriodKey) => {
    if (p === period) return
    setPeriod(p)
    // The period stays in the address, so a reload prints the same pack.
    window.history.replaceState(window.history.state, '', '#/report/programme?period=' + p)
  }

  if (error != null) return <ErrorBox error={error} what="The programme report" />

  return (
    <TooltipProvider>
      <div className="rpt hc">
        <div className="rpt-bar" role="toolbar" aria-label="Report">
          <span className="rpt-bar-title">Programme report</span>
          <span className="rpt-seg" role="group" aria-label="Period">
            {PERIODS.map((p) => (
              <button key={p.key} type="button" aria-pressed={period === p.key} onClick={() => choose(p.key)}>
                {p.label}
              </button>
            ))}
          </span>
          <span className="rpt-bar-grow" />
          {/* Back to where the report was opened from (Home, Reports), not
              always Home (Back model B1, B3). */}
          <BackLink className="rpt-back" fallback={{ label: 'Reports', href: '#/reports' }} />
          <button type="button" className="primary" onClick={() => window.print()} disabled={!panel || loading}>
            Print or save as PDF
          </button>
        </div>
        {!panel ? (
          <div className="rpt-sheet">
            <Skeleton lines={14} />
          </div>
        ) : (
          <div className={'rpt-sheets' + (loading ? ' rpt-stale' : '')} aria-busy={loading}>
            <Pack panel={panel} report={report} reportFailed={reportFailed} period={period} me={me} customer={customer} chain={chain} />
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}

type Section = { key: string; title: string; caption?: string; body: ReactNode }

function Pack({
  panel,
  report,
  reportFailed,
  period,
  me,
  customer,
  chain,
}: {
  panel: Panel
  report: Report | null
  reportFailed: boolean
  period: HomePeriodKey
  me: Me
  customer: string
  chain: Chain
}) {
  const generated = report?.generatedAt ?? panel.computedAt
  const from = report?.period.from ?? new Date(new Date(panel.computedAt).getTime() - panel.period.days * 86400000).toISOString()
  const since = dayMonth(report?.previous.to ?? from)
  const days = report?.period.days ?? panel.period.days
  const periodWords = days === 90 ? 'last quarter' : 'last ' + days + ' days'

  const sections = useMemo(() => {
    const out: Section[] = []

    // ------------------------------------------------ 1 executive summary
    if (panel.health || report) {
      out.push({
        key: 'summary',
        title: 'Executive summary',
        caption: 'Health score now, and what the committee should know first.',
        body: <Summary panel={panel} report={report} period={period} since={since} periodWords={periodWords} />,
      })
    }

    // ------------------------------------------------------ 2 key figures
    if (report && report.figures.length > 0) {
      out.push({
        key: 'figures',
        title: 'Key figures',
        caption: 'Each figure now, against its value on ' + since + ', the end of the previous ' + days + ' days.',
        body: (
          <div className="rpt-figs">
            {report.figures.map((f) => (
              <FigureCard key={f.key} label={f.label} now={f.now} previous={f.previous} direction={f.direction} since={since} />
            ))}
          </div>
        ),
      })
    }

    // ------------------------------------------------------------ 3 risk
    if (panel.bands && panel.tierBands) {
      out.push({
        key: 'risk',
        title: 'Risk across vendors',
        caption: 'Vendors on the register by risk band and tier, now.',
        body: <RiskSection panel={panel} report={report} />,
      })
    }

    // -------------------------------------------------------- 4 findings
    if (report || panel.findingsWeekly || panel.findingsTrend) {
      out.push({
        key: 'findings',
        title: 'Findings',
        caption: 'Open findings by severity and age, and how many were raised and closed.',
        body: <FindingsSection panel={panel} report={report} periodWords={periodWords} />,
      })
    }

    // ----------------------------------------------------- 5 assessments
    if (report || panel.pipeline) {
      out.push({
        key: 'assessments',
        title: 'Assessments',
        caption: 'How much of the estate carries a current score, and what is late.',
        body: <AssessmentsSection panel={panel} report={report} days={days} />,
      })
    }

    // ----------------------------------------------------- 6 offboarding
    if (report) {
      out.push({
        key: 'offboarding',
        title: 'Offboarding',
        caption: 'Exits proposed and offboarding, where each stands, and the evidence recorded.',
        body: <OffboardingSection report={report} />,
      })
    }

    // ------------------------------------------------------ 7 regulatory
    if (report?.regulatory) {
      out.push({
        key: 'regulatory',
        title: 'Regulatory deadlines and accepted risks',
        caption: 'Reporting duties on open incidents, accepted risks running out, and exception closures that are late, now.',
        body: <RegulatorySection regulatory={report.regulatory} />,
      })
    }

    // ------------------------------------------------------ 8 purchasing
    if (report?.purchasing) {
      const pur = report.purchasing
      out.push({
        key: 'purchasing',
        title: 'Purchasing clearance',
        caption: 'Purchase requests from ' + pur.system + ', by the answer standing on their vendor.',
        body: (
          <div className="rpt-card rpt-keep">
            <StackBar
              title={'Purchase requests in ' + pur.system}
              parts={[
                { key: 'cleared', label: 'Cleared', count: pur.cleared, color: 'var(--ok-solid)' },
                { key: 'pending', label: 'Waiting for an assessment', count: pur.pending, color: 'var(--warn-solid)' },
                { key: 'blocked', label: 'Blocked', count: pur.blocked, color: 'var(--crit-solid)' },
              ]}
            />
          </div>
        ),
      })
    }

    // ------------------------------------------------------- 9 deadlines
    if (panel.deadlines) {
      // A panel whose period scopes it plans deadlines over the period; an
      // older one always over thirty days.
      const horizon = panel.findingsTrend ? panel.period.days : 30
      out.push({
        key: 'deadlines',
        title: 'Deadlines',
        caption: 'Dates falling due in the next ' + horizon + ' days, and dates already missed.',
        body: <DeadlinesSection panel={panel} horizon={horizon} />,
      })
    }
    return out
  }, [panel, report, period, since, periodWords, days])

  // The pages a printed pack breaks into, each starting on a new sheet.
  const plan = [['summary', 'figures'], ['risk'], ['findings'], ['assessments', 'offboarding'], ['regulatory', 'purchasing', 'deadlines']]
  const numbered = new Map(sections.map((s, i) => [s.key, i + 1]))
  const sheets = plan
    .map((keys) => sections.filter((s) => keys.includes(s.key)))
    .filter((group) => group.length > 0)

  return (
    <>
      <Cover report={report} customer={customer} me={me} panel={panel} generated={generated} from={from} />
      {reportFailed && (
        <p className="rpt-degraded" role="status">
          This API does not serve the full programme report yet, so the pack shows what Home reads.
        </p>
      )}
      {sheets.map((group) => (
        <article key={group.map((s) => s.key).join('+')} className="rpt-sheet">
          <RunningHead customer={customer} />
          {group.map((s) => (
            <section key={s.key} className="rpt-section" aria-labelledby={'rpt-h-' + s.key}>
              <header className="rpt-h">
                <span className="rpt-h-n" aria-hidden="true">
                  {String(numbered.get(s.key)).padStart(2, '0')}
                </span>
                <div>
                  <h2 id={'rpt-h-' + s.key}>{s.title}</h2>
                  {s.caption && <p>{s.caption}</p>}
                </div>
              </header>
              {s.body}
            </section>
          ))}
        </article>
      ))}
      <article className="rpt-sheet">
        <RunningHead customer={customer} />
        <section className="rpt-section" aria-labelledby="rpt-h-appendix">
          <header className="rpt-h">
            <span className="rpt-h-n" aria-hidden="true">
              A
            </span>
            <div>
              <h2 id="rpt-h-appendix">Appendix: how this report was reached</h2>
              <p>The health score's six parts, the evidence chain, and when the figures were read.</p>
            </div>
          </header>
          <Appendix panel={panel} report={report} chain={chain} generated={generated} />
        </section>
      </article>
    </>
  )
}

// ----------------------------------------------------------------- cover

function Cover({
  report,
  customer,
  me,
  panel,
  generated,
  from,
}: {
  report: Report | null
  customer: string
  me: Me
  panel: Panel
  generated: string
  from: string
}) {
  const legal = report?.tenant.legalName ?? me.tenant?.legalName ?? ''
  const prepared = [report?.preparedBy.name, report?.preparedBy.persona ?? panel.persona?.name].filter(Boolean).join(', ')
  return (
    <article className="rpt-sheet rpt-cover" aria-labelledby="rpt-title">
      <span className="rpt-art" aria-hidden="true">
        <i />
        <i />
        <i />
        <b />
        <b />
      </span>
      <div className="rpt-cover-top">
        <img className="rpt-logo rpt-logo-light" src="/foctta-header-logo.png" alt="FOCTTA" />
        <img className="rpt-logo rpt-logo-dark" src="/foctta-header-logo-dark.png" alt="" aria-hidden="true" />
        <span className="rpt-conf">Confidential</span>
      </div>

      <div className="rpt-cover-main">
        <span className="rpt-marks" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <p className="rpt-eyebrow">Third-party risk programme report</p>
        <h1 id="rpt-title" className="rpt-client">
          {customer}
        </h1>
        {legal && legal !== customer && <p className="rpt-legal">{legal}</p>}
        <p className="rpt-span">{span(from, generated)}</p>
        {report && <p className="rpt-compare">Compared with {span(report.previous.from, report.previous.to)}</p>}
      </div>

      <dl className="rpt-cover-meta">
        <div>
          <dt>Prepared by</dt>
          <dd>{prepared || 'This account'}</dd>
        </div>
        <div>
          <dt>Generated</dt>
          <dd>{istStamp(generated)}</dd>
        </div>
        <div>
          <dt>Report ID</dt>
          <dd className="rpt-mono">{report?.reportId ?? '-'}</dd>
        </div>
        <div>
          <dt>Classification</dt>
          <dd>Confidential. For the risk committee and the people it names.</dd>
        </div>
      </dl>

      <footer className="rpt-cover-foot">
        <span>FOCTTA TPRM</span>
        <span>Every figure is read from the register when the report is generated.</span>
      </footer>
    </article>
  )
}

function RunningHead({ customer }: { customer: string }) {
  return (
    <div className="rpt-run" aria-hidden="true">
      <img className="rpt-run-logo rpt-logo-light" src="/foctta-header-logo.png" alt="" />
      <img className="rpt-run-logo rpt-logo-dark" src="/foctta-header-logo-dark.png" alt="" />
      <span>Third-party risk programme report</span>
      <span className="rpt-run-client">{customer}</span>
    </div>
  )
}

// -------------------------------------------------------- 1 summary

const TONE_WORD: Record<string, string> = { crit: 'Act', warn: 'Watch', info: 'Note', ok: 'On track' }

function Summary({
  panel,
  report,
  period,
  since,
  periodWords,
}: {
  panel: Panel
  report: Report | null
  period: HomePeriodKey
  since: string
  periodWords: string
}) {
  const h = panel.health
  const score = h?.score ?? report?.method.score ?? 0
  const word = h?.word ?? report?.method.word ?? ''
  const labels = useMemo(() => {
    const at = new Date(panel.computedAt).getTime()
    const n = h?.series.length ?? 0
    return Array.from({ length: n }, (_, i) => dayMonth(new Date(at - (n - 1 - i) * STEP_DAYS[period] * 86400000)))
  }, [panel.computedAt, h, period])

  let change = 'No health reading on ' + since + ' to compare with.'
  if (h?.change != null) {
    change = h.change === 0 ? 'No change since ' + since + '.' : (h.change > 0 ? 'Up ' : 'Down ') + Math.abs(h.change) + ' points since ' + since + '.'
  }
  const readings = (h?.series ?? []).filter((v) => v != null).length

  return (
    <div className="rpt-summary">
      <div className="rpt-card rpt-health rpt-keep">
        <Ring score={score} word={word} />
        <div className="rpt-health-words">
          <b>Health score</b>
          <span>{change}</span>
          {h && readings >= 2 && (
            <div className="rpt-health-trend">
              <Sparkline series={h.series} labels={labels} />
              <small>
                Health, {periodWords}
                {h.seriesSince ? ', tracked from ' + calendarDate(h.seriesSince) : ''}
              </small>
            </div>
          )}
        </div>
      </div>
      {report && (
        <div className="rpt-card rpt-keep">
          <h3 className="rpt-card-h">Headlines</h3>
          {report.headlines.length === 0 ? (
            <p className="rpt-quiet">Nothing in the register calls for the committee's attention.</p>
          ) : (
            <ul className="rpt-headlines">
              {report.headlines.map((x, i) => (
                <li key={i} className={'rpt-hl ' + x.tone}>
                  <span className="rpt-hl-tone">
                    <i aria-hidden="true" />
                    {TONE_WORD[x.tone] ?? 'Note'}
                  </span>
                  {x.href ? <a href={x.href}>{x.text}</a> : <span>{x.text}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

// -------------------------------------------------------------- 3 risk

function RiskSection({ panel, report }: { panel: Panel; report: Report | null }) {
  const bands = panel.bands ?? []
  const grid = panel.tierBands!
  const slices: DonutSlice[] = bands.map((b) => ({
    key: b.key,
    label: b.label,
    count: b.count,
    color: BAND_COLOR[b.key] ?? 'var(--hc-b-none)',
    href: b.href || undefined,
  }))
  const total = bands.reduce((a, b) => a + b.count, 0)
  return (
    <>
      <div className="rpt-two">
        <div className="rpt-card rpt-keep">
          <h3 className="rpt-card-h">By risk band</h3>
          <div className="hc-donutrow">
            <Donut slices={slices} total={total} unit="vendors" title="Vendors by risk band" />
            <Legend slices={slices} />
          </div>
        </div>
        <div className="rpt-card rpt-keep">
          <h3 className="rpt-card-h">By tier and band</h3>
          <TierGrid bands={grid.bands} short={grid.short} rows={grid.rows} />
        </div>
      </div>
      {report?.topVendors && (
        <div className="rpt-block">
          <h3 className="rpt-card-h">The {Math.min(10, report.topVendors.length) || 10} vendors carrying the most risk</h3>
          {report.topVendors.length === 0 ? (
            <p className="rpt-quiet">No vendor on the register carries a band yet.</p>
          ) : (
            <TopVendorsTable vendors={report.topVendors} dateOf={fullDate} />
          )}
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------- 4 findings

function FindingsSection({ panel, report, periodWords }: { panel: Panel; report: Report | null; periodWords: string }) {
  const trend = panel.findingsTrend
  const cols = trend
    ? trend.buckets.map((b) => ({ label: b.label, a: b.raised, b: b.closed, open: b.open }))
    : (panel.findingsWeekly?.weeks ?? []).map((w) => ({ label: w.label, a: w.raised, b: w.closed, open: w.open }))
  const raised = cols.reduce((n, c) => n + c.a, 0)
  const closed = cols.reduce((n, c) => n + c.b, 0)
  const stillOpen = trend?.stillOpen ?? panel.findingsWeekly?.stillOpen ?? 0
  const trendWords = trend ? periodWords : '12 weeks'
  const f = report?.findings
  const open = f ? f.bySeverity.critical + f.bySeverity.high + f.bySeverity.medium + f.bySeverity.low : 0

  return (
    <>
      {f && (
        <div className="rpt-two">
          <div className="rpt-card rpt-keep">
            <h3 className="rpt-card-h">
              Open by severity <span className="rpt-count hc-num">{formatIN(open)} open</span>
            </h3>
            <SeverityBars counts={f.bySeverity} title="Open findings by severity" />
          </div>
          <div className="rpt-card rpt-keep">
            <h3 className="rpt-card-h">How long they have been open</h3>
            <SeverityKey />
            <AgeingBars buckets={f.ageing} title="Open findings by days open" />
          </div>
        </div>
      )}
      {cols.length > 0 && (
        <div className="rpt-card rpt-keep">
          <h3 className="rpt-card-h">
            Raised and closed, {trendWords}{' '}
            <span className="rpt-count">
              {formatIN(raised)} raised, {formatIN(closed)} closed, {formatIN(stillOpen)} still open
            </span>
          </h3>
          <div className="hc-keyrow rpt-key">
            <span>
              <i style={{ background: 'var(--hc-s1)' }} />
              Raised
            </span>
            <span>
              <i style={{ background: 'var(--hc-s2)' }} />
              Closed
            </span>
          </div>
          <WeeklyColumns
            weeks={cols}
            names={['Raised', 'Closed']}
            colors={['var(--hc-s1)', 'var(--hc-s2)']}
            openLabel="Still open"
            unit={trend?.unit ?? 'week'}
            title={'Findings raised and closed, ' + trendWords}
          />
        </div>
      )}
      {f?.oldest && (
        <div className="rpt-block">
          <h3 className="rpt-card-h">The oldest open findings</h3>
          {f.oldest.length === 0 ? (
            <p className="rpt-quiet">No finding is open.</p>
          ) : (
            <div className="rpt-table-wrap">
              <table className="rpt-table">
                <thead>
                  <tr>
                    <th scope="col">Finding</th>
                    <th scope="col">Vendor</th>
                    <th scope="col">Severity</th>
                    <th scope="col">Raised</th>
                    <th scope="col" className="n">
                      Days open
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {f.oldest.map((o, i) => {
                    const sev = SEVERITIES.find((s) => s.key === o.severity.toLowerCase())
                    return (
                      <tr key={i}>
                        <td className="rpt-strong" title={o.title}>
                          <span className="rpt-clamp">{o.title}</span>
                        </td>
                        <td>{o.vendor}</td>
                        <td className="nowrap">
                          <i className="rpt-swatch" style={{ background: sev?.color }} />
                          {sev?.label ?? o.severity}
                        </td>
                        <td className="nowrap">{calendarDate(o.openedAt)}</td>
                        <td className="n">{formatIN(o.days)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  )
}

// ------------------------------------------------------- 5 assessments

function AssessmentsSection({ panel, report, days }: { panel: Panel; report: Report | null; days: number }) {
  const a = report?.assessments
  return (
    <>
      <div className="rpt-two">
        {a && (
          <div className="rpt-card rpt-health rpt-keep">
            {a.coveragePercent == null ? (
              <p className="rpt-quiet">No vendor is live yet, so there is nothing to cover.</p>
            ) : (
              <>
                <Ring
                  score={a.coveragePercent}
                  suffix="%"
                  word="scored"
                  label={'Coverage ' + a.coveragePercent + ' percent: ' + a.scoredCurrent + ' of ' + a.liveVendors + ' live vendors scored'}
                />
                <div className="rpt-health-words">
                  <b>Coverage</b>
                  <span>
                    {formatIN(a.scoredCurrent)} of {plural(a.liveVendors, 'live vendor carries', 'live vendors carry')} a current score.
                  </span>
                  <span>{a.notScored === 0 ? 'Every live vendor is scored.' : plural(a.notScored, 'vendor has', 'vendors have') + ' none.'}</span>
                </div>
              </>
            )}
          </div>
        )}
        {panel.pipeline && (
          <div className="rpt-card rpt-keep">
            <h3 className="rpt-card-h">Questionnaires</h3>
            <Pipeline steps={panel.pipeline.steps.map((s) => ({ key: s.key, label: s.label, count: s.count }))} title="Questionnaires" />
            <p className="rpt-note">
              {panel.pipeline.overdue === 0
                ? 'None overdue.'
                : formatIN(panel.pipeline.overdue) + ' overdue' +
                  (panel.pipeline.overdueVendor ? ', the longest ' + panel.pipeline.overdueVendor + ' at ' + (panel.pipeline.overdueDays ?? 0) + ' days.' : '.')}
            </p>
          </div>
        )}
      </div>
      {a?.overdue && (
        <div className="rpt-block">
          <h3 className="rpt-card-h">
            Overdue questionnaires{' '}
            {a.overdueTotal > a.overdue.length && (
              <span className="rpt-count">
                the first {a.overdue.length} of {formatIN(a.overdueTotal)}
              </span>
            )}
          </h3>
          {a.overdue.length === 0 ? (
            <p className="rpt-quiet">No questionnaire is past its due date.</p>
          ) : (
            <div className="rpt-table-wrap">
              <table className="rpt-table">
                <thead>
                  <tr>
                    <th scope="col">Vendor</th>
                    <th scope="col">Reference</th>
                    <th scope="col">Due</th>
                    <th scope="col" className="n">
                      Days late
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {a.overdue.map((o, i) => (
                    <tr key={i}>
                      <td className="rpt-strong">{o.vendor}</td>
                      <td className="rpt-mono">{o.reference}</td>
                      <td className="nowrap">{calendarDate(o.dueOn)}</td>
                      <td className="n">{formatIN(o.daysLate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {a?.reassessmentsDue && (
        <div className="rpt-block">
          <h3 className="rpt-card-h">
            Reassessments due in the next {days} days{' '}
            {a.reassessmentsDueTotal > a.reassessmentsDue.length && (
              <span className="rpt-count">
                the first {a.reassessmentsDue.length} of {formatIN(a.reassessmentsDueTotal)}
              </span>
            )}
          </h3>
          {a.reassessmentsDue.length === 0 ? (
            <p className="rpt-quiet">No reassessment falls due in the next {days} days.</p>
          ) : (
            <div className="rpt-table-wrap">
              <table className="rpt-table">
                <thead>
                  <tr>
                    <th scope="col">Vendor</th>
                    <th scope="col">Due</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {a.reassessmentsDue.map((r, i) => (
                    <tr key={i}>
                      <td className="rpt-strong">{r.vendor}</td>
                      <td className="nowrap">{calendarDate(r.dueOn)}</td>
                      <td>
                        <span className={'rpt-state ' + (r.overdue ? 'crit' : 'info')}>{r.overdue ? 'Overdue' : 'Due'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  )
}

// ------------------------------------------------------- 6 offboarding

function OffboardingSection({ report }: { report: Report }) {
  const o = report.offboarding
  const inFlight = o.byStage.reduce((n, s) => n + s.count, 0)
  return (
    <>
      <div className="rpt-card rpt-keep">
        <h3 className="rpt-card-h">
          Exits by stage <span className="rpt-count">{plural(inFlight, 'exit', 'exits')} proposed or offboarding</span>
        </h3>
        <StageCounts stages={o.byStage} title="Exits by stage" />
        <div className="rpt-evidence">
          <span className="rpt-card-sub">Evidence across exits offboarding</span>
          <EvidenceMeter recorded={o.evidence.recorded} required={o.evidence.required} />
        </div>
      </div>
      {o.blocked && (
        <div className="rpt-block">
          <h3 className="rpt-card-h">
            Exits held <span className="rpt-count">{plural(o.blockedTotal, 'exit', 'exits')}, {formatIN(o.blockedOnEvidence)} on missing evidence</span>
          </h3>
          {o.blocked.length === 0 ? (
            <p className="rpt-quiet">No offboarding exit is held.</p>
          ) : (
            <div className="rpt-table-wrap">
              <table className="rpt-table">
                <thead>
                  <tr>
                    <th scope="col">Vendor</th>
                    <th scope="col">Stage</th>
                    <th scope="col">Held by</th>
                    <th scope="col">Kind</th>
                  </tr>
                </thead>
                <tbody>
                  {o.blocked.map((b) => (
                    <tr key={b.vendorId}>
                      <td className="rpt-strong">{b.vendor}</td>
                      <td>{b.stage}</td>
                      <td>{b.blocker}</td>
                      <td className="nowrap">
                        <span className={'rpt-state ' + (b.missingEvidence ? 'warn' : 'crit')}>{b.missingEvidence ? 'Evidence missing' : 'Declined'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  )
}

// -------------------------------------------------------- 7 regulatory

function clockColor(c: HomeClock): string {
  if (c.overdue) return 'var(--crit-solid)'
  if (c.withoutDelay || c.elapsed >= 0.75) return 'var(--warn-solid)'
  return 'var(--info-solid)'
}

function acceptanceWords(expired: number, expiring: number): string {
  const late =
    expired === 0 ? 'No accepted risk has expired' : plural(expired, 'accepted risk has expired and is', 'accepted risks have expired and are') + ' still approved'
  const soon = expiring === 0 ? 'none expires within a week' : plural(expiring, 'expires', 'expire') + ' within a week'
  return late + '; ' + soon + '.'
}

// The due diligence exceptions whose corrective action is late, in the same
// sentence shape as the acceptances.
function closureWords(overdue: number, dueSoon: number): string {
  const late = overdue === 0 ? 'No exception closure is overdue' : plural(overdue, 'exception closure is', 'exception closures are') + ' overdue'
  const soon = dueSoon === 0 ? 'none falls due within a week' : plural(dueSoon, 'falls', 'fall') + ' due within a week'
  return late + '; ' + soon + '.'
}

function RegulatorySection({ regulatory }: { regulatory: NonNullable<Report['regulatory']> }) {
  const incidents = regulatory.incidentClocks
  const acc = regulatory.acceptances
  const closures = regulatory.closures
  const waiting = regulatory.waitingForYou ?? []
  const clocks = [...(incidents ?? []), ...(acc?.items ?? []), ...(closures?.items ?? [])]
  return (
    <div className="rpt-card">
      {waiting.length > 0 && (
        <div className="rpt-block">
          <p className="rpt-note rpt-lead">{plural(waiting.length, 'exception is', 'exceptions are')} waiting for you.</p>
          <ul className="rpt-clocks dd-waiting">
            {waiting.map((x) => (
              <li key={x.id} className="rpt-keep">
                <div>
                  <b>{x.href ? <a href={x.href}>{x.title}</a> : x.title}</b>
                  <span>
                    {x.vendorName}
                    {x.severityName ? ', ' + x.severityName.toLowerCase() + ' severity' : ''}
                  </span>
                </div>
                <span className="rpt-state warn">{x.act}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {acc && (
        <p className="rpt-note rpt-lead">
          {acceptanceWords(acc.expired, acc.expiring)}
        </p>
      )}
      {closures && (closures.overdue > 0 || closures.dueSoon > 0) && (
        <p className="rpt-note rpt-lead">{closureWords(closures.overdue, closures.dueSoon)}</p>
      )}
      {clocks.length === 0 ? (
        <p className="rpt-quiet">{incidents ? 'No reporting duty is running on an open incident.' : 'No accepted risk is running out.'}</p>
      ) : (
        <ul className="rpt-clocks">
          {clocks.map((c, i) => (
            <li key={i} className="rpt-keep">
              <ClockRing elapsed={c.elapsed} color={clockColor(c)} />
              <div>
                <b>{c.title}</b>
                <span>{c.subject}</span>
              </div>
              <span className={'rpt-state ' + (c.overdue ? 'crit' : c.withoutDelay ? 'warn' : 'info')}>{c.words}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// --------------------------------------------------------- 9 deadlines

function DeadlinesSection({ panel, horizon }: { panel: Panel; horizon: number }) {
  const deadlines = panel.deadlines ?? []
  const today = new Date(panel.computedAt)
  return (
    <>
      <div className="rpt-card rpt-keep">
        <DeadlineStrip
          items={deadlines.map((d) => ({ date: d.date, title: d.title, subject: d.subject, tone: d.tone, word: d.word, href: d.href }))}
          today={today}
          span={horizon}
        />
      </div>
      {deadlines.length === 0 ? (
        <p className="rpt-quiet">Nothing falls due in the next {horizon} days.</p>
      ) : (
        <div className="rpt-table-wrap rpt-block">
          <table className="rpt-table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">What</th>
                <th scope="col">Who</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {deadlines.map((d, i) => (
                <tr key={i}>
                  <td className="nowrap">{calendarDate(d.date)}</td>
                  <td className="rpt-strong">{d.title}</td>
                  <td>{d.subject}</td>
                  <td className="nowrap">
                    <span className={'rpt-state ' + d.tone}>{d.word}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------- appendix

function Appendix({ panel, report, chain, generated }: { panel: Panel; report: Report | null; chain: Chain; generated: string }) {
  const components = report?.method.components ?? panel.health?.components ?? []
  const score = report?.method.score ?? panel.health?.score
  const word = report?.method.word ?? panel.health?.word
  return (
    <>
      {components.length > 0 && (
        <div className="rpt-block">
          <h3 className="rpt-card-h">
            How the health score is calculated{' '}
            {score != null && (
              <span className="rpt-count">
                100 less the points lost: {score}, {word?.toLowerCase()}
              </span>
            )}
          </h3>
          <div className="rpt-table-wrap">
            <table className="rpt-table rpt-method">
              <thead>
                <tr>
                  <th scope="col">Part</th>
                  <th scope="col">Rule</th>
                  <th scope="col">Now</th>
                  <th scope="col" className="n">
                    Points lost
                  </th>
                </tr>
              </thead>
              <tbody>
                {components.map((c) => (
                  <tr key={c.key}>
                    <td className="rpt-strong nowrap">{HEALTH_PART[c.key] ?? c.key}</td>
                    <td className="rpt-quiet">{HEALTH_RULE[c.key] ?? ''}</td>
                    <td>{c.label}</td>
                    <td className="n nowrap">
                      <span className="rpt-lost" aria-hidden="true">
                        <i style={{ width: (c.max > 0 ? Math.min(1, c.lost / c.max) : 0) * 100 + '%' }} />
                      </span>
                      {healthPoints(c.lost)} of {c.max}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="rpt-card rpt-chain rpt-keep">
        <h3 className="rpt-card-h">Evidence chain</h3>
        {chain == null ? (
          <p className="rpt-quiet">Verifying the evidence chain.</p>
        ) : 'refused' in chain ? (
          <p className="rpt-quiet">{chain.refused}</p>
        ) : chain.verdict.ok ? (
          <dl className="rpt-facts">
            <div>
              <dt>Chain head</dt>
              <dd className="rpt-mono rpt-hash">{chain.verdict.headHash ?? '-'}</dd>
            </div>
            <div>
              <dt>Events</dt>
              <dd className="hc-num">{formatIN(chain.verdict.events)}</dd>
            </div>
            <div>
              <dt>Verified</dt>
              <dd>
                <span className="rpt-state ok">Intact</span> {istStamp(chain.at)}
              </dd>
            </div>
          </dl>
        ) : (
          <p className="rpt-state crit">
            The evidence chain did not verify at event {chain.verdict.breakAt ?? '?'}
            {chain.verdict.detail ? ': ' + chain.verdict.detail : ''}. Checked at {istStamp(chain.at)}.
          </p>
        )}
      </div>
      <p className="rpt-read">Figures read from the register at {istStamp(generated)}.</p>
    </>
  )
}
